import React from 'react'
import { validatePositioningProps } from '../../../helpers/validatePositioningProps'
import { AriaProps, OmitInternalProperties, PositioningProps } from '../../types'
import { Tooltip } from '../../feedback/tooltip/Tooltip'
import { AnchorOnlyProps, ButtonOnlyProps } from '../types'
import { useAnchorProps, useButtonProps } from '../hooks'
import {
  BaseButtonBox,
  BaseButtonIcon,
  baseButtonProps,
  BaseButtonVariant,
  baseButtonVariantProps,
} from '../base-button/BaseButton'

type CommonProps = {
  INTERNAL_nativeOnClick?: (event: React.MouseEvent) => void
  ariaProps?: AriaProps
  'data-id'?: string
  'data-test-id'?: string
  hasTooltip?: boolean
  icon: React.ComponentType
  isDisabled?: boolean
  label: string
  size: 5 | 6 | 7 | 8
  variant: 'major' | 'moderate' | 'minor' | 'ghost' | 'caution'
} & PositioningProps

export type ButtonProps = CommonProps & ButtonOnlyProps
export type AnchorProps = CommonProps & AnchorOnlyProps

type Props = ButtonProps | AnchorProps
export const IconButtonInternal = ({ hasTooltip = true, ...props }: Props) => {
  if (props.as === 'a') {
    if (!hasTooltip) {
      return <IconButtonAsAnchor {...props} />
    }
    return (
      <Tooltip content={props.label}>
        <IconButtonAsAnchor {...props} />
      </Tooltip>
    )
  }
  if (!hasTooltip) {
    return <IconButtonAsButton {...props} />
  }
  return (
    <Tooltip content={props.label}>
      <IconButtonAsButton {...props} />
    </Tooltip>
  )
}

type PublicProps = OmitInternalProperties<ButtonProps> | OmitInternalProperties<AnchorProps>

export const IconButtonPublic = (props: PublicProps) => <IconButtonInternal {...props} />

const IconButtonAsButton = ({
  INTERNAL_nativeOnClick,
  ariaProps,
  onClick,
  size,
  label,
  icon,
  as: _as, // eslint-disable-line @typescript-eslint/no-unused-vars
  type,
  'data-id': dataId,
  'data-test-id': dataTestId,
  variant,
  isDisabled,
  ...positioningProps
}: ButtonProps) => {
  validatePositioningProps(positioningProps, 'IconButton')

  const { ref, buttonProps } = useButtonProps({
    onClick,
    type,
    INTERNAL_nativeOnClick,
    'aria-label': label,
    isDisabled,
    ...ariaProps,
  })

  const commonProps = getCommonProps(variant, size)

  return (
    <BaseButtonBox
      as='button'
      ref={ref}
      data-id={dataId}
      data-test-id={dataTestId}
      {...commonProps}
      {...positioningProps}
      {...buttonProps}
    >
      <Icon icon={icon} />
    </BaseButtonBox>
  )
}

const IconButtonAsAnchor = ({
  onClick,
  size,
  label,
  icon,
  'data-id': dataId,
  'data-test-id': dataTestId,
  variant,
  ariaProps,
  INTERNAL_nativeOnClick,
  as: _as, // eslint-disable-line @typescript-eslint/no-unused-vars
  rel,
  href,
  target,
  isDisabled,
  ...positioningProps
}: AnchorProps) => {
  validatePositioningProps(positioningProps, 'IconButton')

  const { anchorProps, ref } = useAnchorProps({
    ...ariaProps,
    onClick,
    INTERNAL_nativeOnClick,
    'aria-label': label,
    isDisabled,
    href,
    target,
    rel,
  })
  const commonProps = getCommonProps(variant, size)

  return (
    <BaseButtonBox
      as='a'
      ref={ref}
      data-id={dataId}
      data-test-id={dataTestId}
      {...commonProps}
      {...positioningProps}
      {...anchorProps}
    >
      <Icon icon={icon} />
    </BaseButtonBox>
  )
}

const buttonVariantProps: Record<
  Props['variant'],
  Omit<(typeof baseButtonVariantProps)[BaseButtonVariant], 'color' | 'hoverColor'>
> = {
  major: baseButtonVariantProps.major,
  moderate: {
    bg: 'bg/button-moderate-default',
    hoverBg: 'bg/button-moderate-hovered',
    iconColor: 'fg/moderate',
    iconHoverColor: 'fg/moderate',
  },
  minor: baseButtonVariantProps.minor,
  ghost: {
    bg: 'transparent',
    hoverBg: 'bg/button-minor-hovered',
    iconColor: 'fg/moderate',
    iconHoverColor: 'fg/moderate',
  },
  caution: {
    ...baseButtonVariantProps.minor,
    iconColor: 'fg/caution',
    iconHoverColor: 'fg/caution',
  },
}

const getCommonProps = (variant: Props['variant'], size: Props['size']) => {
  const { bg, border, hoverBg, iconColor, iconHoverColor } = buttonVariantProps[variant]
  const borderRadius = getBorderRadius(size)

  return {
    ...baseButtonProps,
    borderRadius,
    px: 0,
    py: 0,
    w: size,
    h: size,
    bg,
    boxShadow: border,
    hoverBg,
    iconColor,
    iconHoverColor,
    flexShrink: 0,
  } as const
}
const Icon = ({ icon }: Pick<Props, 'icon'>) => {
  return <BaseButtonIcon icon={icon} w={4} h={4} />
}

const getBorderRadius = (size: Props['size']) => {
  if (size === 8) {
    return 10
  }
  if (size === 5) {
    return 6
  }
  return 8
}
